/* eslint react-hooks/exhaustive-deps: "warn" */

import { type ReactNode, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './RegisModes.module.scss';

type Translate = (key: string) => string;

type SpecRow = {
  term: string;
  value: ReactNode;
};

type Mode = {
  kicker: string;
  title: string;
  description: string;
  spec: SpecRow[];
  primary?: boolean;
};

const getModes = (t: Translate): Mode[] => [
  {
    kicker: t('Deterministic'),
    title: t('Structured endpoints'),
    description: t(
      'You name the company and the field. KYB, officers, shareholders, group structure, financials, credit and bank verification each have their own path and a fixed response shape.',
    ),
    spec: [
      { term: t('Input'), value: t('Identifier or filters') },
      { term: t('Output'), value: t('Fixed JSON schema') },
      { term: t('Provenance'), value: <span className={styles.lit}>include_provenance</span> },
      { term: t('Best for'), value: t('Pipelines, batch runs') },
    ],
  },
  {
    kicker: t('Agentic'),
    title: t('Regis query'),
    description: t(
      'You ask the question. Regis resolves the entities, decides which registers and fields answer it, retrieves them, and returns the answer with its sources. Multi-step questions resolve in one call.',
    ),
    spec: [
      {
        term: t('Input'),
        value: (
          <>
            <span className={styles.lit}>prompt</span> {t('as a string')}
          </>
        ),
      },
      { term: t('Output'), value: t('Answer plus sources') },
      { term: t('Provenance'), value: t('Attached by default') },
      { term: t('Best for'), value: t('Analysts, agents, apps') },
    ],
    primary: true,
  },
];

export const RegisModes = () => {
  const { t } = useInt();
  const rootRef = useRef<HTMLDivElement>(null);
  const visible = useIntersection(rootRef, { threshold: 0.1 });

  const modes = getModes(t);

  return (
    <Section className={styles.root}>
      <div ref={rootRef} className={cn(styles.content, visible && styles.contentIn)}>
        <div className={styles.head}>
          <TitleBlock
            align="left"
            title={t('One dataset, two access patterns.')}
            description={t(
              'Global Database’s registry data is reachable two ways on the same API key. The structured endpoints take a company identifier and a named field, and return a fixed response shape. Regis takes a question and works out for itself which registers and fields answer it. Both read the same records.',
            )}
          />
        </div>

        <div className={styles.modes}>
          {modes.map((mode) => (
            <div key={mode.title} className={cn(styles.mode, mode.primary && styles.modePrimary)}>
              {mode.primary && <span className={styles.badge}>{t('Natural language')}</span>}

              <span className={cn(styles.modeKicker, mode.primary && styles.modeKickerPrimary)}>{mode.kicker}</span>
              <h3 className={styles.modeTitle}>{mode.title}</h3>
              <p className={styles.modeText}>{mode.description}</p>

              <dl className={styles.spec}>
                {mode.spec.map((row) => (
                  <div key={row.term}>
                    <dt>{row.term}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
